// The art palette: every colour a generator may use is a palette.json token or one of its derived shades
// (UX §6.2, research-artAudio §1 "Deriving each item's swatch"): base, shade (L−.14, C+.02, h−10°),
// light/rim (L+.12, C−.03, h+10°), outline (L .25, C×.6, h), glow (.85, .20, h) and hi (base mixed with
// white at `highlightAlpha`, the opaque form of the 50% white highlight line). Blaze orange (#fe6a00) is
// reserved for muzzle tips.
import type { PaletteData } from '../data/schema.ts';
import { hex, mix, type Oklch, oklchToRgb, parseHex, type Rgb } from './color.ts';

export type Token = string;

export interface Swatch {
  base: string;
  shade: string;
  light: string;
  outline: string;
  glow: string;
  hi: string;
}

export const SWATCH_KEYS = ['base', 'shade', 'light', 'outline', 'glow', 'hi'] as const;

export interface ArtPalette {
  /** Derived swatch of a token (throws on an unknown token, so typos fail the bake). */
  sw(token: Token): Swatch;
  /** A token's base hex. */
  hex(token: Token): string;
  /** `rgba(…)` of a token's base (or a swatch key) at alpha a — only for non-opaque layers. */
  rgba(token: Token, a: number, key?: keyof Swatch): string;
  tokens(): Token[];
  /** Tier ramp token for tier t (1-based). */
  tier(t: number): Token;
  /** Blaze orange hex and its token (reserved for muzzle tips). */
  blaze: string;
  blazeTok: Token;
  white: string;
  rules: PaletteData['rules'];
  art: PaletteData['art'];
  /** Every colour a generator may draw opaque (tests/art: ≥ 95% of opaque pixels within ΔE 12). */
  derivedHexes(): string[];
}

function clampL(l: number, minL: number): number {
  return Math.min(1, Math.max(minL, l));
}

export function deriveSwatch(
  [L, C, h]: Oklch,
  rules: PaletteData['rules'],
  art: PaletteData['art'],
): Swatch {
  const wrap = (x: number) => ((x % 360) + 360) % 360;
  const base = oklchToRgb([L, C, h]);
  const white: Rgb = [255, 255, 255];
  return {
    base: hex(base),
    shade: hex(
      oklchToRgb([
        clampL(L + rules.shadeDL, art.minL),
        Math.max(0, C + rules.shadeDC),
        wrap(h + rules.shadeDH),
      ]),
    ),
    light: hex(
      oklchToRgb([
        clampL(L + rules.rimDL, art.minL),
        Math.max(0, C + rules.rimDC),
        wrap(h + rules.rimDH),
      ]),
    ),
    outline: hex(oklchToRgb([rules.outlineL, C * rules.outlineChromaMult, h])),
    glow: hex(oklchToRgb([art.glowL, art.glowC, h])),
    hi: hex(mix(base, white, rules.highlightAlpha)),
  };
}

export function buildPalette(data: PaletteData): ArtPalette {
  const swatches: Record<string, Swatch> = {};
  for (const [k, c] of Object.entries(data.colors)) {
    swatches[k] = deriveSwatch(c.oklch, data.rules, data.art);
  }
  const sw = (t: Token): Swatch => {
    const s = swatches[t];
    if (!s) throw new Error(`art: unknown palette token "${t}"`);
    return s;
  };
  const blazeTok = sw(data.blaze);
  return {
    sw,
    hex: (t) => sw(t).base,
    rgba(t, a, key = 'base') {
      const [r, g, b] = parseHex(sw(t)[key]);
      return `rgba(${r},${g},${b},${a})`;
    },
    tokens: () => Object.keys(swatches),
    tier(t) {
      const tok = data.tierRamp[Math.min(data.tierRamp.length, Math.max(1, t)) - 1];
      if (!tok) throw new Error('art: empty tier ramp');
      return tok;
    },
    blaze: blazeTok.base,
    blazeTok: data.blaze,
    white: '#ffffff',
    rules: data.rules,
    art: data.art,
    derivedHexes() {
      const out = new Set<string>(['#ffffff']);
      for (const s of Object.values(swatches)) for (const k of SWATCH_KEYS) out.add(s[k]);
      return [...out].sort();
    },
  };
}

/** palette.json baked for Unity / tools: token → derived swatch hexes. */
export function paletteExport(p: ArtPalette): Record<string, Swatch> {
  const out: Record<string, Swatch> = {};
  for (const t of p.tokens().sort()) out[t] = p.sw(t);
  return out;
}

/** palette.json colour id → its reference hex (#rrggbb): the renderer's tints and the DOM FX colours. */
export function paletteHex(
  colors: Readonly<Record<string, { hex: string }>>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(colors)) out[k] = v.hex;
  return out;
}
